import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { calendarKey } from '@/features/calendar/api';
import { useMemo } from 'react';
import { z } from 'zod';
import { errorMessage, request, requestVoid } from '@/shared/api/client';
import { toast } from '@/shared/ui/toastStore';
import {
  activityItemSchema,
  attachmentSchema,
  conversationDetailsSchema,
  conversationSchema,
  directoryEntrySchema,
  docNodeSchema,
  fileEntrySchema,
  invitedMemberSchema,
  memberSchema,
  messagePageSchema,
  messageSchema,
  myMembershipSchema,
  reactionSchema,
  revisionSchema,
  todoSchema,
  searchResultSchema,
  sidebarSchema,
  threadSchema,
  threadSummarySchema,
  type Reaction,
  type ThreadSummary,
  workspaceSchema,
  type ActivityItem,
  type Attachment,
  type Conversation,
  type DocNode,
  type LinkPreviewSettings,
  type Me,
  type Quote,
  type Member,
  type MyMembership,
  type NotifyLevel,
  type NotifyPref,
  type Role,
  type Sidebar,
  type Thread,
} from '@/shared/api/schemas';
import { meKey } from '@/features/auth/api';
import { dlpWarning, heldSchema } from '@/features/dlp/api';
import { forgetSent, restoreToComposer } from '@/features/composer/unsend';
import { t } from '@/shared/i18n';
import { registerNonHumans } from '@/shared/ui/avatarKinds';
import { useWorkspace } from './context';
import { holdUnread, releaseUnread } from './activity';
import { keys } from './keys';
import { applyOptimistic, settleOptimistic, type ReactionTarget } from './optimisticReactions';
import { discardLocal, markLocal, placeLocal, QueuedOffline, sendBody, sendOrQueue, settleSent } from './sendQueue';
import { fromPage, PENDING_SEQ, removeMessage, type LocalMessage, type LocalPage } from './localMessages';

// ——— Чтение ———

export function useSidebar() {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.sidebar(workspace.id), queryFn: () => request('GET', `${api}/conversations`, sidebarSchema), staleTime: 30_000 });
}

/** Вид беседы из боковой панели. Перерисовывает, только когда вид меняется. */
export function useConversationKind(cid: string) {
  const { workspace, api } = useWorkspace();
  return useQuery({
    queryKey: keys.sidebar(workspace.id),
    queryFn: () => request('GET', `${api}/conversations`, sidebarSchema),
    staleTime: 30_000,
    select: (d) => d.conversations.find((c) => c.id === cid)?.kind,
  }).data;
}

/** Секретная ли беседа: из неё и в неё пересылать нельзя. */
export function useConversationSecret(cid: string): boolean {
  const { workspace, api } = useWorkspace();
  return (
    useQuery({
      queryKey: keys.sidebar(workspace.id),
      queryFn: () => request('GET', `${api}/conversations`, sidebarSchema),
      staleTime: 30_000,
      select: (d) => d.conversations.find((c) => c.id === cid)?.secretUntil != null,
    }).data ?? false
  );
}

/** Скрыты ли у меня служебные строки этого канала («Больше не показывать такие уведомления»). */
export function useHidesSystem(cid: string): boolean {
  const { workspace, api } = useWorkspace();
  return (
    useQuery({
      queryKey: keys.sidebar(workspace.id),
      queryFn: () => request('GET', `${api}/conversations`, sidebarSchema),
      staleTime: 30_000,
      select: (d) => d.conversations.find((c) => c.id === cid)?.hideSystem ?? false,
    }).data ?? false
  );
}

export function useMembers() {
  const { workspace, api } = useWorkspace();
  return useQuery({
    queryKey: keys.members(workspace.id),
    queryFn: async () => {
      const list = await request('GET', `${api}/members`, z.array(memberSchema));
      // Приложения и боты рисуются скруглённым квадратом, круглые аватарки только у людей — и у людей из Slack (мост).
      registerNonHumans(list.filter((m) => m.bot && !m.bridge).map((m) => m.userId));
      return list;
    },
    // Без опроса раз в минуту: отметки «в сети» и состав приходят событиями presence и members.changed,
    // а после обрыва связи realtime сам обновляет всё пространство (invalidateQueries по keys.all).
    staleTime: 60_000,
  });
}

/** Люди пространства по id пользователя. */
export function useMembersMap(): ReadonlyMap<string, Member> {
  const members = useMembers();
  return useMemo(() => new Map((members.data ?? []).map((m) => [m.userId, m])), [members.data]);
}

export function useDirectory() {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.directory(workspace.id), queryFn: () => request('GET', `${api}/channels`, z.array(directoryEntrySchema)), staleTime: 60_000 });
}

export function useConversationDetails(cid: string, enabled = true) {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.details(workspace.id, cid), queryFn: () => request('GET', `${api}/conversations/${cid}`, conversationDetailsSchema), enabled });
}

/** Первая страница ленты беседы: один и тот же запрос у открытия беседы и у заранее подгрузки. */
export const messagesQuery = (wid: string, api: string, cid: string) => ({
  queryKey: keys.messages(wid, cid),
  queryFn: async (): Promise<LocalPage> => fromPage(await request('GET', `${api}/conversations/${cid}/messages?limit=50`, messagePageSchema)),
  // Лента обновляется событиями в реальном времени.
  staleTime: Infinity,
});

export function useMessages(cid: string) {
  const { workspace, api } = useWorkspace();
  return useQuery(messagesQuery(workspace.id, api, cid));
}

/** Заранее подгрузить ленту беседы (наведение на строку, простой после старта): к открытию сообщения уже в памяти, без спиннера. */
export function prefetchMessages(client: QueryClient, wid: string, api: string, cid: string): Promise<unknown> {
  return client.query(messagesQuery(wid, api, cid)).catch(() => undefined);
}

export function useThread(rootId: string | null) {
  const { workspace, api } = useWorkspace();
  return useQuery({
    queryKey: keys.thread(workspace.id, rootId ?? ''),
    queryFn: () => request('GET', `${api}/messages/${rootId ?? ''}/thread`, threadSchema),
    enabled: rootId !== null,
    staleTime: Infinity,
  });
}

export function usePins(cid: string) {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.pins(workspace.id, cid), queryFn: () => request('GET', `${api}/conversations/${cid}/pins`, z.array(messageSchema)) });
}

export function useFiles(cid: string) {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.files(workspace.id, cid), queryFn: () => request('GET', `${api}/conversations/${cid}/files`, z.array(fileEntrySchema)) });
}

/** Сохранённое: закладки на сообщения (в «Главной»). */
export function useSaved() {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.saved(workspace.id), queryFn: () => request('GET', `${api}/saved`, z.array(todoSchema)) });
}

/** Todo: дела из сообщений и свои, с напоминаниями. */
export function useTodos() {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.todos(workspace.id), queryFn: () => request('GET', `${api}/todos`, z.array(todoSchema)) });
}

/** Где сообщение: в Todo, в сохранённом или нигде — чтобы подсветить кнопку в ленте. */
export function useMessageMarks(): ReadonlyMap<string, 'todo' | 'saved'> {
  const todos = useTodos();
  const saved = useSaved();
  return useMemo(() => {
    const map = new Map<string, 'todo' | 'saved'>();
    for (const t of saved.data ?? []) if (t.message !== null) map.set(t.message.id, 'saved');
    for (const t of todos.data ?? []) if (t.message !== null) map.set(t.message.id, 'todo');
    return map;
  }, [todos.data, saved.data]);
}

export function useThreads() {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.threads(workspace.id), queryFn: () => request('GET', `${api}/threads`, z.array(threadSummarySchema)) });
}

export function useActivity() {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.activity(workspace.id), queryFn: () => request('GET', `${api}/activity`, z.array(activityItemSchema)) });
}

/** Убранное из «Активности» — только для вида, в котором оно включено. */
export function useClearedActivity(enabled: boolean) {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: [...keys.activity(workspace.id), 'cleared'], queryFn: () => request('GET', `${api}/activity?cleared=1`, z.array(activityItemSchema)), enabled });
}

/** Непрочитанное в «Активности»: упоминания — счётчиком, реакции — точкой, как в Slack. */
export function useActivityUnread(): { readonly mentions: number; readonly reactions: number } {
  const activity = useActivity();
  const list = activity.data ?? [];
  // Упоминания — счётчиком; реакции, ответы в тредах, приглашения и напоминания — точкой.
  const counted = (a: ActivityItem) => a.type === 'mention' || a.type === 'keyword';
  return { mentions: list.filter((a) => !a.read && counted(a)).length, reactions: list.filter((a) => !a.read && !counted(a)).length };
}

export type ActivityState = 'read' | 'unread' | 'cleared';

/** Пункты «Активности»: прочитано, снова непрочитано, убрать. Меняется сразу, сервер догоняет. */
export function useActivityMark() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly keys: readonly string[]; readonly state: ActivityState }) => requestVoid('POST', `${api}/activity/marks`, { body: { keys: input.keys, state: input.state } }),
    onMutate: (input) => {
      const patch = (old: readonly ActivityItem[]) => (input.state === 'cleared' ? old.filter((a) => !input.keys.includes(a.key)) : old.map((a) => (input.keys.includes(a.key) ? { ...a, read: input.state === 'read' } : a)));
      client.setQueryData<ActivityItem[]>(keys.activity(workspace.id), (old) => (old === undefined ? old : patch(old)));
      // Свои виды (страницы с сервера) — так же сразу.
      client.setQueriesData<{ pages: { items: ActivityItem[]; next: string | null }[]; pageParams: unknown[] }>({ queryKey: [...keys.activity(workspace.id), 'feed'] }, (old) => (old === undefined ? old : { ...old, pages: old.pages.map((p) => ({ ...p, items: patch(p.items) })) }));
    },
    onSuccess: (_, input) => {
      void client.invalidateQueries({ queryKey: [...keys.activity(workspace.id), 'cleared'] });
      void client.invalidateQueries({ queryKey: [...keys.activity(workspace.id), 'feed'] });
      // Вернули убранный пункт — его нет в списке, перечитываем.
      const list = client.getQueryData<ActivityItem[]>(keys.activity(workspace.id)) ?? [];
      if (input.state !== 'cleared' && input.keys.some((k) => !list.some((a) => a.key === k))) void client.invalidateQueries({ queryKey: keys.activity(workspace.id) });
    },
    onError: (error) => {
      toast(errorMessage(error));
      void client.invalidateQueries({ queryKey: keys.activity(workspace.id) });
    },
  });
}

/** «Отметить всё прочитанным» и «Очистить всё». */
export function useActivityAll() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (state: 'read' | 'cleared') => requestVoid('POST', `${api}/activity/all`, { body: { state } }),
    onMutate: (state) => {
      client.setQueryData<ActivityItem[]>(keys.activity(workspace.id), (old) => (old === undefined ? old : state === 'cleared' ? [] : old.map((a) => ({ ...a, read: true }))));
    },
    onError: (error) => {
      toast(errorMessage(error));
      void client.invalidateQueries({ queryKey: keys.activity(workspace.id) });
    },
  });
}

export function useSearch(q: string, conversationId: string, authorId: string) {
  const { workspace, api } = useWorkspace();
  const params = new URLSearchParams();
  if (q !== '') params.set('q', q);
  if (conversationId !== '') params.set('conversationId', conversationId);
  if (authorId !== '') params.set('authorId', authorId);
  return useQuery({
    queryKey: keys.search(workspace.id, q, conversationId, authorId),
    queryFn: () => request('GET', `${api}/search?${params.toString()}`, searchResultSchema),
    enabled: q.trim().length >= 2 || authorId !== '',
  });
}

export function useMessage(id: string | null) {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: keys.message(workspace.id, id ?? ''), queryFn: () => request('GET', `${api}/messages/${id ?? ''}`, messageSchema), enabled: id !== null });
}

// ——— Помощники кэша ———

export function patchSidebar(client: QueryClient, wid: string, cid: string, patch: (c: Conversation) => Conversation): void {
  client.setQueryData<Sidebar>(keys.sidebar(wid), (old) => (old === undefined ? old : { ...old, conversations: old.conversations.map((c) => (c.id === cid ? patch(c) : c)) }));
}

export function putConversation(client: QueryClient, wid: string, conversation: Conversation): void {
  client.setQueryData<Sidebar>(keys.sidebar(wid), (old) => {
    if (old === undefined) return old;
    const exists = old.conversations.some((c) => c.id === conversation.id);
    return { ...old, conversations: exists ? old.conversations.map((c) => (c.id === conversation.id ? conversation : c)) : [...old.conversations, conversation] };
  });
}

function updateMyMembership(client: QueryClient, membership: MyMembership): void {
  client.setQueryData<Me | null>(meKey, (old) =>
    old === null || old === undefined ? old : { ...old, memberships: old.memberships.map((m) => (m.workspace.id === membership.workspace.id ? { ...membership, external: m.external } : m)) },
  );
}

// ——— Изменения ———

export function useCreateChannel() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; private: boolean; description: string; memberIds: string[]; templateId?: string }) => request('POST', `${api}/channels`, conversationSchema, { body: input }),
    onSuccess: (c) => {
      putConversation(client, workspace.id, c);
      void client.invalidateQueries({ queryKey: keys.directory(workspace.id) });
    },
  });
}

export function useOpenDirect() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) => request('POST', `${api}/direct`, conversationSchema, { body: { userIds } }),
    onSuccess: (c) => {
      putConversation(client, workspace.id, c);
    },
  });
}

/** Действия с беседой, после которых обновляются панель, сведения и каталог. */
export function useConversationAction() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { cid: string; action: 'join' | 'leave' | 'archive' | 'unarchive' | 'delete' }) => {
      if (input.action === 'delete') return requestVoid('DELETE', `${api}/conversations/${input.cid}`);
      return requestVoid('POST', `${api}/conversations/${input.cid}/${input.action}`);
    },
    onSuccess: (_, input) => {
      void client.invalidateQueries({ queryKey: keys.sidebar(workspace.id) });
      void client.invalidateQueries({ queryKey: keys.details(workspace.id, input.cid) });
      void client.invalidateQueries({ queryKey: keys.directory(workspace.id) });
    },
  });
}

export function useUpdateConversation(cid: string) {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; topic?: string; description?: string; private?: boolean }) => request('PATCH', `${api}/conversations/${cid}`, conversationSchema, { body: input }),
    onSuccess: (c) => {
      putConversation(client, workspace.id, c);
      void client.invalidateQueries({ queryKey: keys.details(workspace.id, cid) });
    },
  });
}

export function useChannelMembers(cid: string) {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  const done = () => {
    void client.invalidateQueries({ queryKey: keys.details(workspace.id, cid) });
    void client.invalidateQueries({ queryKey: keys.sidebar(workspace.id) });
  };
  const add = useMutation({
    mutationFn: (userIds: string[]) => request('POST', `${api}/conversations/${cid}/members`, z.object({ allMemberIds: z.array(z.string()) }), { body: { userIds } }),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: (userId: string) => request('DELETE', `${api}/conversations/${cid}/members/${userId}`, z.object({ allMemberIds: z.array(z.string()) })),
    onSuccess: done,
  });
  return { add, remove };
}

export function usePrefs(cid: string) {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { starred?: boolean; muted?: boolean; mutedUntil?: string | null; hidden?: boolean; hideSystem?: boolean; notify?: NotifyPref; sectionId?: string | null }) => request('PATCH', `${api}/conversations/${cid}/prefs`, conversationSchema, { body: input }),
    onMutate: (input) => {
      patchSidebar(client, workspace.id, cid, (c) => ({ ...c, ...input }));
    },
    // Отказ сервера — возвращаем строку, которую убрали заранее: канал не должен пропасть без следа.
    onError: () => {
      void client.invalidateQueries({ queryKey: keys.sidebar(workspace.id) });
    },
    onSuccess: (c, input) => {
      putConversation(client, workspace.id, c);
      // Скрыли или вернули канал — «Все каналы» показывает это же состояние.
      if (input.hidden !== undefined) void client.invalidateQueries({ queryKey: keys.directory(workspace.id) });
    },
  });
}

/** Переложить беседу (перетаскиванием в боковой панели): в избранное, в свой раздел или обратно. */
export function usePlaceConversation() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ cid, ...input }: { cid: string; starred: boolean; sectionId: string | null }) => request('PATCH', `${api}/conversations/${cid}/prefs`, conversationSchema, { body: input }),
    onMutate: ({ cid, ...input }) => {
      patchSidebar(client, workspace.id, cid, (c) => ({ ...c, ...input }));
    },
    onSuccess: (c) => {
      putConversation(client, workspace.id, c);
    },
  });
}

export function useMarkRead() {
  const { workspace, api, user } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { cid: string; seq: number; unread?: boolean }) => requestVoid('POST', `${api}/conversations/${input.cid}/read`, { body: { seq: input.seq, unread: input.unread ?? false } }),
    onMutate: (input) => {
      // Отмеченное непрочитанным открытая лента не читает обратно; явное «прочитано» снимает отметку.
      if (input.unread === true) holdUnread(input.cid);
      else releaseUnread(input.cid);
      // Непрочитанное видно сразу: если последнее сообщение от собеседника — точка и жирное имя до ответа сервера,
      // точное число придёт с обновлением списка.
      const fromOther = (c: Conversation) => c.lastMessage !== null && c.lastMessage.kind !== 'system' && c.lastMessage.authorId !== user.id;
      patchSidebar(client, workspace.id, input.cid, (c) =>
        input.unread === true
          ? { ...c, lastReadSeq: input.seq, unread: c.unread > 0 || !fromOther(c) ? c.unread : 1, mentions: c.unread > 0 || !fromOther(c) || c.kind === 'public' || c.kind === 'private' ? c.mentions : 1 }
          : { ...c, lastReadSeq: Math.max(c.lastReadSeq, input.seq), unread: input.seq >= c.lastSeq ? 0 : c.unread, mentions: input.seq >= c.lastSeq ? 0 : c.mentions },
      );
      // Дочитанное упоминание в канале прочитано и в «Активности» — как на сервере.
      if (input.unread !== true) {
        client.setQueryData<ActivityItem[]>(keys.activity(workspace.id), (old) =>
          old?.some((a) => !a.read && a.type === 'mention' && a.message.conversationId === input.cid && a.message.threadRootId === null && a.message.seq <= input.seq) === true
            ? old.map((a) => (a.type === 'mention' && a.message.conversationId === input.cid && a.message.threadRootId === null && a.message.seq <= input.seq ? { ...a, read: true } : a))
            : old,
        );
      }
    },
    // Ждём свежий список: колбэк onSuccess у вызова (TanStack ждёт этот) уже видит, получилось ли — в переписке
    // могут быть только свои сообщения.
    onSuccess: async (_, input) => {
      if (input.unread === true) await client.invalidateQueries({ queryKey: keys.sidebar(workspace.id) });
    },
    // Не вышло отметить непрочитанным — снимаем удержание и возвращаем список с сервера.
    onError: (_, input) => {
      if (input.unread !== true) return;
      releaseUnread(input.cid);
      void client.invalidateQueries({ queryKey: keys.sidebar(workspace.id) });
    },
  });
}

/** «Печатает…»; с `editingMessageId` — «правит» общий текст. */
export function sendTyping(api: string, cid: string, threadRootId: string | null, editingMessageId: string | null = null): void {
  void requestVoid('POST', `${api}/conversations/${cid}/typing`, { body: editingMessageId === null ? { threadRootId } : { threadRootId, editingMessageId } }).catch(() => undefined);
}

export interface SendInput {
  readonly cid: string;
  readonly content: DocNode;
  readonly text: string;
  readonly threadRootId: string | null;
  readonly alsoInChannel: boolean;
  readonly attachments: readonly Attachment[];
  readonly clientId: string;
  /** Отправить как «Общий текст» — править смогут все в беседе. */
  readonly collab?: boolean;
  /** Цитата: на какое сообщение ответ и какой кусок его текста. */
  readonly quote?: Quote;
  /** «Защита данных»: правила, о которых автор видел плашку и всё равно отправил. */
  readonly dlpAck?: readonly string[];
  /** Как показать превью ссылки: размер, место, ссылка. Нет — по умолчанию. */
  readonly linkPreview?: LinkPreviewSettings | null;
}

/** Ответ на отправку: сообщение или «задержано до проверки» (202, «Защита данных»). */
export const sentSchema = z.union([messageSchema, heldSchema]);

/** Отправка с мгновенным показом: сообщение сразу в ленте, после ответа сервера заменяется настоящим. */
export function useSendMessage() {
  const { workspace, api, user } = useWorkspace();
  const client = useQueryClient();
  const place = (input: SendInput, message: LocalMessage) => {
    placeLocal(client, workspace.id, input, message);
  };
  const discard = (input: SendInput) => {
    discardLocal(client, workspace.id, input);
  };
  return useMutation({
    // Без сети сообщение не ждёт на паузе в памяти, а ложится в очередь на устройстве (sendQueue).
    networkMode: 'always',
    mutationFn: (input: SendInput) =>
      sendOrQueue(workspace.id, user.id, api, input, () => request('POST', `${api}/conversations/${input.cid}/messages`, sentSchema, { body: sendBody(input) })),
    onMutate: (input) => {
      const now = new Date().toISOString();
      place(input, {
        id: input.clientId,
        clientId: input.clientId,
        status: 'sending',
        conversationId: input.cid,
        seq: PENDING_SEQ,
        authorId: user.id,
        systemCode: null,
        systemData: {},
        content: input.content,
        text: input.text,
        threadRootId: input.threadRootId,
        replyCount: 0,
        lastReplyAt: null,
        lastReplySeq: 0,
        replyUserIds: [],
        alsoInChannel: input.alsoInChannel,
        linkPreview: input.linkPreview ?? null,
        shared: null,
        quote: input.quote ?? null,
        secret: null,
        pollId: null,
        approvalId: null,
        formId: null,
        editedAt: null,
        collab: input.collab === true,
        collabEditorIds: input.collab === true ? [user.id] : [],
        lastEditorId: null,
        revisionCount: input.collab === true ? 1 : 0,
        editVersion: 0,
        deleted: false,
        pinnedAt: null,
        pinnedById: null,
        createdAt: now,
        reactions: [],
        attachments: [...input.attachments],
      });
    },
    onSuccess: (result, input) => {
      if ('held' in result) {
        // Задержано: в ленте его нет, пока ответственный не проверит.
        discard(input);
        return;
      }
      settleSent(client, workspace.id, input, result);
    },
    onError: (error, input) => {
      if (error instanceof QueuedOffline) {
        // Связи нет: сообщение в очереди на устройстве, в ленте с часами.
        markLocal(client, workspace.id, input, 'queued');
        return;
      }
      if (dlpWarning(error) !== null) {
        // Плашку «Защиты данных» автор не видел: текст вернётся в поле, черновик в ленте не нужен.
        discard(input);
        return;
      }
      markLocal(client, workspace.id, input, 'failed');
    },
  });
}

/** Убрать неотправленное сообщение из ленты. */
export function useDiscardPending() {
  const { workspace } = useWorkspace();
  const client = useQueryClient();
  return (cid: string, clientId: string, threadRootId: string | null) => {
    client.setQueryData<LocalPage>(keys.messages(workspace.id, cid), (old) => (old === undefined ? old : removeMessage(old, clientId)));
    if (threadRootId !== null) {
      client.setQueryData<Thread>(keys.thread(workspace.id, threadRootId), (old) => (old === undefined ? old : { ...old, replies: old.replies.filter((r) => r.id !== clientId) }));
    }
  };
}

/** Правка сообщения. Для общего текста `baseVersion` — номер правки, с которой начали: если текст уже изменили, сервер ответит 409. */
export function useEditMessage() {
  const { api } = useWorkspace();
  return useMutation({
    mutationFn: (input: { id: string; content: DocNode; baseVersion?: number; dlpAck?: readonly string[]; attachmentIds?: readonly string[] | undefined }) =>
      request('PATCH', `${api}/messages/${input.id}`, messageSchema, { body: { content: input.content, dlpAck: input.dlpAck ?? [], attachmentIds: input.attachmentIds, ...(input.baseVersion === undefined ? {} : { baseVersion: input.baseVersion }) } }),
  });
}

/** Разрешить или запретить собеседникам править своё сообщение («Общий текст»). */
export function useSetCollab() {
  const { api } = useWorkspace();
  return useMutation({ mutationFn: (input: { id: string; collab: boolean }) => request('PATCH', `${api}/messages/${input.id}`, messageSchema, { body: { collab: input.collab } }) });
}

/** Версии общего текста — для «Правок» и окна истории; `stamp` (время последней правки) в ключе — новая правка подтягивается сама. */
export function useRevisions(id: string, stamp: string, enabled: boolean) {
  const { workspace, api } = useWorkspace();
  return useQuery({
    queryKey: [...keys.message(workspace.id, id), 'revisions', stamp],
    queryFn: () => request('GET', `${api}/messages/${id}/revisions`, z.array(revisionSchema)),
    enabled,
    staleTime: Infinity,
    placeholderData: (previous) => previous,
  });
}

/** «Повторить» под сообщением, не дошедшим до Slack: сервер ставит доставку заново, пометка исчезает по событию. */
export function useRetrySlack() {
  const { api } = useWorkspace();
  return useMutation({ mutationFn: (id: string) => requestVoid('POST', `${api}/messages/${id}/slack-retry`) });
}

export function useDeleteMessage() {
  const { api } = useWorkspace();
  return useMutation({ mutationFn: (id: string) => requestVoid('DELETE', `${api}/messages/${id}`) });
}

const restoredSchema = z.object({ content: docNodeSchema.nullable(), text: z.string(), attachments: z.array(attachmentSchema) });

/** Отмена отправки: сообщение исчезает у всех, а текст и файлы возвращаются в поле ввода. */
export function useUnsend() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly id: string; readonly cid: string; readonly threadRootId: string | null }) => request('POST', `${api}/messages/${input.id}/unsend`, restoredSchema),
    onSuccess: (restored, input) => {
      forgetSent(input.id);
      client.setQueryData<LocalPage>(keys.messages(workspace.id, input.cid), (old) => (old === undefined ? old : removeMessage(old, input.id)));
      if (input.threadRootId !== null) {
        client.setQueryData<Thread>(keys.thread(workspace.id, input.threadRootId), (old) => (old === undefined ? old : { ...old, replies: old.replies.filter((r) => r.id !== input.id) }));
      }
      restoreToComposer(input.cid, input.threadRootId, restored);
      toast(t('Отправка отменена — сообщение снова в поле ввода'));
    },
    onError: (error, input) => {
      forgetSent(input.id);
      toast(errorMessage(error));
    },
  });
}

interface ReactInput {
  readonly id: string;
  readonly emoji: string;
  readonly conversationId: string;
  readonly threadRootId: string | null;
  readonly reactions: readonly Reaction[];
}

function reactionTarget(wid: string, i: ReactInput): ReactionTarget {
  return { wid, messageId: i.id, conversationId: i.conversationId, threadRootId: i.threadRootId };
}

export function useReact() {
  const { api, workspace, user } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ReactInput) => request('POST', `${api}/messages/${input.id}/reactions`, z.array(reactionSchema), { body: { emoji: input.emoji } }),
    // Реакция видна в тот же кадр; сервер подтверждает фоном, ошибка — тихий откат.
    onMutate: (input) => {
      applyOptimistic(client, reactionTarget(workspace.id, input), input.emoji, user.id, input.reactions);
    },
    onSuccess: (reactions, input) => {
      settleOptimistic(client, reactionTarget(workspace.id, input), { ok: true, reactions });
    },
    onError: (_e, input) => {
      settleOptimistic(client, reactionTarget(workspace.id, input), { ok: false });
    },
  });
}

export function usePin() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  const pin = useMutation({
    mutationFn: (input: { id: string; pinned: boolean }) => request(input.pinned ? 'POST' : 'DELETE', `${api}/messages/${input.id}/pin`, messageSchema),
    onSuccess: (m) => {
      void client.invalidateQueries({ queryKey: keys.pins(workspace.id, m.conversationId) });
    },
    onError: (e, input) => {
      const why = errorMessage(e);
      toast(input.pinned ? t('Не удалось закрепить сообщение. {why}', { why }) : t('Не удалось открепить сообщение. {why}', { why }), {
        action: { label: t('Повторить действие'), onClick: () => { pin.mutate(input); } },
      });
    },
  });
  return pin;
}

/** Сообщение → в Todo или в сохранённое (kind), с напоминанием или без; kind: null — убрать отовсюду. */
export function useSave() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; kind: 'todo' | 'saved' | null; remindAt?: Date | null }) =>
      input.kind === null
        ? requestVoid('DELETE', `${api}/messages/${input.id}/save`)
        : request('POST', `${api}/messages/${input.id}/save`, todoSchema, { body: { kind: input.kind, ...(input.remindAt === undefined ? {} : { remindAt: input.remindAt === null ? null : input.remindAt.toISOString() }) } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.saved(workspace.id) });
      void client.invalidateQueries({ queryKey: keys.todos(workspace.id) });
    },
  });
}

/** Действия со списком Todo. */
export function useTodoActions() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  const refresh = () => {
    void client.invalidateQueries({ queryKey: keys.todos(workspace.id) });
    void client.invalidateQueries({ queryKey: keys.saved(workspace.id) });
  };
  const add = useMutation({
    mutationFn: (input: { title: string; remindAt: Date | null }) => request('POST', `${api}/todos`, todoSchema, { body: { title: input.title, remindAt: input.remindAt?.toISOString() ?? null } }),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, remindAt, ...rest }: { id: string; done?: boolean; title?: string; kind?: 'todo' | 'saved'; remindAt?: Date | null; priority?: number; note?: string | null }) =>
      request('PATCH', `${api}/todos/${id}`, todoSchema, { body: { ...rest, ...(remindAt === undefined ? {} : { remindAt: remindAt === null ? null : remindAt.toISOString() }) } }),
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: (id: string) => requestVoid('DELETE', `${api}/todos/${id}`), onSuccess: refresh });
  const cleanup = useMutation({
    mutationFn: (action: 'to_saved' | 'overdue_done' | 'clear_open' | 'clear_done') => request('POST', `${api}/todos/cleanup`, z.object({ count: z.number().int() }), { body: { action } }),
    onSuccess: refresh,
  });
  return { add, update, remove, cleanup };
}

export function useThreadActions() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  const refresh = () => {
    void client.invalidateQueries({ queryKey: keys.threads(workspace.id) });
    void client.invalidateQueries({ queryKey: keys.sidebar(workspace.id) });
  };
  const read = useMutation({ mutationFn: (rootId: string) => requestVoid('POST', `${api}/messages/${rootId}/thread/read`), onSuccess: refresh });
  // Со страницы «Треды»: метка и точка в боковой панели гаснут сразу, а сам список не перезагружается — новые ответы остаются на виду.
  const readInPlace = useMutation({
    mutationFn: (rootId: string) => requestVoid('POST', `${api}/messages/${rootId}/thread/read`),
    onMutate: (rootId) => {
      client.setQueryData<ThreadSummary[]>(keys.threads(workspace.id), (old) => old?.map((x) => (x.root.id === rootId ? { ...x, unread: false } : x)));
    },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.sidebar(workspace.id) }),
    onError: () => client.invalidateQueries({ queryKey: keys.threads(workspace.id) }),
  });
  const follow = useMutation({
    mutationFn: (input: { rootId: string; following: boolean }) => requestVoid('POST', `${api}/messages/${input.rootId}/thread/follow`, { body: { following: input.following } }),
    onSuccess: (_, input) => {
      refresh();
      client.setQueryData<Thread>(keys.thread(workspace.id, input.rootId), (old) => (old === undefined ? old : { ...old, following: input.following }));
    },
  });
  return { read, readInPlace, follow };
}

export function useUpload() {
  const { api } = useWorkspace();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return request('POST', `${api}/files`, attachmentSchema, { body: form });
    },
  });
}

export function useUpdateMe() {
  const { api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title?: string | null;
      statusEmoji?: string | null;
      statusText?: string | null;
      statusExpiresAt?: string | null;
      presence?: 'auto' | 'away';
      dndUntil?: string | null;
      dndOpen?: boolean;
      focusUntil?: string | null;
      focusOpen?: boolean;
      notify?: NotifyLevel;
    }) => request('PATCH', `${api}/me`, myMembershipSchema, { body: input }),
    onSuccess: (membership, input) => {
      updateMyMembership(client, membership);
      void client.invalidateQueries({ queryKey: keys.members(membership.workspace.id) });
      // «Фокус» и «Не беспокоить» ставят событие в календарь — не ждём сокет, перечитываем сразу.
      if ('focusUntil' in input || 'focusOpen' in input || 'dndUntil' in input || 'dndOpen' in input) {
        void client.invalidateQueries({ queryKey: calendarKey(membership.workspace.id) });
      }
    },
  });
}

export function useAdminMembers() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  const refresh = () => {
    void client.invalidateQueries({ queryKey: keys.members(workspace.id) });
  };
  const invite = useMutation({
    mutationFn: (input: { email: string; name: string; title: string; role: Role }) => request('POST', `${api}/members`, invitedMemberSchema, { body: input }),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: (input: { id: string; role: Role; title: string | null }) => request('PATCH', `${api}/members/${input.id}`, memberSchema, { body: { role: input.role, title: input.title } }),
    onSuccess: refresh,
  });
  const toggle = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => request('POST', `${api}/members/${input.id}/${input.active ? 'reactivate' : 'deactivate'}`, memberSchema),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => requestVoid('DELETE', `${api}/members/${id}`),
    onSuccess: refresh,
  });
  return { invite, update, toggle, remove };
}

export function useRenameWorkspace() {
  const { api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => request('PATCH', api, workspaceSchema, { body: { name } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: meKey });
    },
  });
}

/** Что меняем в настройках пространства: превью ссылок, домены для входа через Google или адрес Bridge. */
export type WorkspaceSettingsInput = { readonly linkPreviews: boolean } | { readonly googleDomains: readonly string[] } | { readonly bridgeUrl: string | null } | { readonly inviteRequests: boolean } | { readonly requireTwoFactor: boolean } | { readonly publicDocLinks: 'off' | 'admins' | 'members' } | { readonly secretChats?: 'members' | 'groups' | 'admins' | 'off'; readonly secretChatGroups?: readonly string[] };

/** Настройки пространства: превью ссылок, домены для входа через Google. */
export function useWorkspaceSettings() {
  const { api } = useWorkspace();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkspaceSettingsInput) => request('PATCH', `${api}/settings`, workspaceSchema, { body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: meKey });
    },
    onError: (e) => {
      toast(errorMessage(e));
    },
  });
}

export interface NewWorkspaceInput {
  readonly name: string;
  /** Рабочий домен: пускать всех с такой почтой (вход через Google); null — только по приглашению. */
  readonly domain: string | null;
  readonly invites: readonly { readonly email: string; readonly name: string }[];
}

export interface NewWorkspaceResult {
  readonly membership: MyMembership;
  /** Кого позвать не удалось (адрес → причина) и сохранился ли домен: пространство при этом уже создано. */
  readonly failed: readonly { readonly email: string; readonly reason: string }[];
  readonly domainFailed: boolean;
}

/**
 * Создаёт пространство целиком одним действием мастера: само пространство, домен для входа и письма с приглашениями.
 * Название обязательно, остальное необязательно и не отменяет создание: то, что не получилось, возвращаем списком.
 */
export function useCreateWorkspace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewWorkspaceInput): Promise<NewWorkspaceResult> => {
      const membership = await request('POST', '/api/workspaces', myMembershipSchema, { body: { name: input.name } });
      const base = `/api/workspaces/${membership.workspace.id}`;
      let domainFailed = false;
      if (input.domain !== null) {
        try {
          await request('PATCH', `${base}/settings`, workspaceSchema, { body: { googleDomains: [input.domain] } });
        } catch {
          domainFailed = true;
        }
      }
      const failed: { email: string; reason: string }[] = [];
      for (const { email, name } of input.invites) {
        try {
          await request('POST', `${base}/members`, invitedMemberSchema, { body: { email, name, title: '', role: 'member' } });
        } catch (error) {
          failed.push({ email, reason: errorMessage(error) });
        }
      }
      return { membership, failed, domainFailed };
    },
    // Переходим в новое пространство только когда список «моих» пространств уже перечитан: иначе страница не находит его и откатывает назад.
    onSuccess: async ({ membership }) => {
      // Новое пространство кладём в кэш сразу и ждём перечитывания: иначе оболочка не находит его среди своих и возвращает в старое.
      client.setQueryData<Me | null>(meKey, (old) => (old === null || old === undefined ? old : { ...old, memberships: [...old.memberships.filter((m) => m.workspace.id !== membership.workspace.id), membership] }));
      await client.invalidateQueries({ queryKey: meKey });
    },
  });
}

